import type { VideoTrack } from '../Player/PlayerModels';

type DiagnosticEventName =
  | 'abort'
  | 'canplay'
  | 'canplaythrough'
  | 'emptied'
  | 'ended'
  | 'error'
  | 'loadstart'
  | 'loadeddata'
  | 'loadedmetadata'
  | 'pause'
  | 'play'
  | 'playing'
  | 'progress'
  | 'ratechange'
  | 'seeking'
  | 'seeked'
  | 'stalled'
  | 'suspend'
  | 'volumechange'
  | 'waiting'
  | 'heartbeat'
  | 'resume_seek';

type DiagnosticEvent = {
  event: DiagnosticEventName;
  at_ms: number;
  current_time: number;
  duration: number | null;
  ready_state: number;
  network_state: number;
  paused: boolean;
  playback_rate: number;
  buffered_ahead_sec: number;
  buffered_ranges: Array<{ start: number; end: number }>;
  extra?: Record<string, unknown>;
};

type PlaybackCounters = {
  abort_count: number;
  buffer_count: number;
  emptied_count: number;
  error_count: number;
  loadstart_count: number;
  pause_count: number;
  play_count: number;
  playing_count: number;
  progress_count: number;
  retry_count: number;
  seek_count: number;
  stalled_count: number;
  suspend_count: number;
  waiting_count: number;
};

const MAX_EVENTS = 80;
const HEARTBEAT_INTERVAL_MS = 10000;
const THROTTLED_EVENT_INTERVAL_MS = 5000;

const observedEvents: DiagnosticEventName[] = [
  'abort',
  'canplay',
  'canplaythrough',
  'emptied',
  'ended',
  'error',
  'loadstart',
  'loadeddata',
  'loadedmetadata',
  'pause',
  'play',
  'playing',
  'progress',
  'ratechange',
  'seeking',
  'seeked',
  'stalled',
  'suspend',
  'volumechange',
  'waiting',
];

const round = (value: number, precision = 3) => {
  if (!Number.isFinite(value)) return null;

  const multiplier = 10 ** precision;
  return Math.round(value * multiplier) / multiplier;
};

const getRanges = (ranges: TimeRanges | undefined) => {
  if (!ranges) return [];

  const output: Array<{ start: number; end: number }> = [];
  for (let index = 0; index < ranges.length; index += 1) {
    output.push({
      start: round(ranges.start(index)) || 0,
      end: round(ranges.end(index)) || 0,
    });
  }

  return output;
};

const getBufferedAhead = (video: HTMLVideoElement) => {
  for (let index = 0; index < video.buffered?.length; index += 1) {
    const start = video.buffered.start(index);
    const end = video.buffered.end(index);

    if (video.currentTime >= start && video.currentTime <= end) {
      return round(end - video.currentTime) || 0;
    }
  }

  return 0;
};

const getPlaybackQuality = (video?: HTMLVideoElement | null) => {
  if (!video) return null;

  const quality = (video as any).getVideoPlaybackQuality?.();
  if (!quality) return null;

  return {
    creation_time: round(quality.creationTime || 0),
    dropped_video_frames: quality.droppedVideoFrames,
    total_video_frames: quality.totalVideoFrames,
  };
};

const getConnectionInfo = () => {
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  if (!connection) return null;

  return {
    downlink_mbps: connection.downlink,
    effective_type: connection.effectiveType,
    rtt_ms: connection.rtt,
    save_data: connection.saveData,
  };
};

const normalizeUrl = (url?: string) => (url || '').split('?')[0];

const getResourceTiming = (url?: string) => {
  if (!url || typeof performance === 'undefined' || !performance.getEntriesByType) return null;

  const normalizedUrl = normalizeUrl(url);
  const entries = performance
    .getEntriesByType('resource')
    .filter((entry) => normalizeUrl(entry.name) === normalizedUrl) as PerformanceResourceTiming[];
  const entry = entries[entries.length - 1];

  if (!entry) return null;

  const durationSec = entry.duration / 1000;
  const transferSize = entry.transferSize || entry.encodedBodySize || 0;

  return {
    name: entry.name,
    duration_ms: round(entry.duration),
    encoded_body_size: entry.encodedBodySize,
    transfer_size: entry.transferSize,
    decoded_body_size: entry.decodedBodySize,
    estimated_bandwidth_mbps: durationSec > 0 && transferSize > 0
      ? round((transferSize * 8) / durationSec / 1000000)
      : null,
  };
};

const serializeError = (error?: MediaError | Record<string, unknown> | null) => {
  if (!error) return null;

  return {
    code: (error as MediaError).code,
    message: (error as MediaError).message,
  };
};

const createSimplePlayerDiagnostics = ({
  contentId,
  fileId,
  sources,
}: {
  contentId: string;
  fileId: string;
  sources: VideoTrack[];
}) => {
  const startedAt = Date.now();
  const events: DiagnosticEvent[] = [];
  const counters: PlaybackCounters = {
    abort_count: 0,
    buffer_count: 0,
    emptied_count: 0,
    error_count: 0,
    loadstart_count: 0,
    pause_count: 0,
    play_count: 0,
    playing_count: 0,
    progress_count: 0,
    retry_count: 0,
    seek_count: 0,
    stalled_count: 0,
    suspend_count: 0,
    waiting_count: 0,
  };
  const lastLoggedAt: Partial<Record<DiagnosticEventName, number>> = {};

  let bufferingStartedAt: number | null = null;
  let maxBufferedAheadSec = 0;
  let maxBufferingMs = 0;
  let totalBufferingMs = 0;
  let heartbeatId: ReturnType<typeof setInterval> | null = null;

  const updateCounters = (event: DiagnosticEventName) => {
    if (event === 'abort') counters.abort_count += 1;
    if (event === 'emptied') counters.emptied_count += 1;
    if (event === 'error') counters.error_count += 1;
    if (event === 'loadstart') {
      counters.loadstart_count += 1;
      counters.retry_count = Math.max(0, counters.loadstart_count - 1);
    }
    if (event === 'pause') counters.pause_count += 1;
    if (event === 'play') counters.play_count += 1;
    if (event === 'playing') counters.playing_count += 1;
    if (event === 'progress') counters.progress_count += 1;
    if (event === 'seeking') counters.seek_count += 1;
    if (event === 'stalled') counters.stalled_count += 1;
    if (event === 'suspend') counters.suspend_count += 1;
    if (event === 'waiting') counters.waiting_count += 1;
    if (event === 'waiting' || event === 'stalled') counters.buffer_count += 1;
  };

  const updateBufferingTime = (event: DiagnosticEventName) => {
    if ((event === 'waiting' || event === 'stalled') && bufferingStartedAt === null) {
      bufferingStartedAt = Date.now();
      return;
    }

    if ((event === 'playing' || event === 'canplay') && bufferingStartedAt !== null) {
      const bufferingMs = Date.now() - bufferingStartedAt;
      totalBufferingMs += bufferingMs;
      maxBufferingMs = Math.max(maxBufferingMs, bufferingMs);
      bufferingStartedAt = null;
    }
  };

  const addEvent = (event: DiagnosticEventName, video?: HTMLVideoElement | null, extra?: Record<string, unknown>) => {
    const now = Date.now();
    const shouldThrottle = event === 'progress' || event === 'heartbeat';
    const lastLogged = lastLoggedAt[event];

    if (
      shouldThrottle &&
      lastLogged !== undefined &&
      now - lastLogged < THROTTLED_EVENT_INTERVAL_MS
    ) {
      return;
    }

    updateCounters(event);
    updateBufferingTime(event);
    lastLoggedAt[event] = now;

    if (!video) return;

    const bufferedAheadSec = getBufferedAhead(video);
    maxBufferedAheadSec = Math.max(maxBufferedAheadSec, bufferedAheadSec);

    events.push({
      event,
      at_ms: now - startedAt,
      current_time: round(video.currentTime) || 0,
      duration: Number.isFinite(video.duration) ? round(video.duration) : null,
      ready_state: video.readyState,
      network_state: video.networkState,
      paused: video.paused,
      playback_rate: video.playbackRate,
      buffered_ahead_sec: bufferedAheadSec,
      buffered_ranges: getRanges(video.buffered),
      extra,
    });

    if (events.length > MAX_EVENTS) events.shift();
  };

  const attach = (video: HTMLVideoElement) => {
    const listeners = observedEvents.map((eventName) => {
      const listener = () => addEvent(eventName, video);
      video.addEventListener(eventName, listener);
      return { eventName, listener };
    });

    heartbeatId = setInterval(() => addEvent('heartbeat', video), HEARTBEAT_INTERVAL_MS);

    return () => {
      listeners.forEach(({ eventName, listener }) => {
        video.removeEventListener(eventName, listener);
      });
      if (heartbeatId) clearInterval(heartbeatId);
      heartbeatId = null;
    };
  };

  const toRawData = (video?: HTMLVideoElement | null, error?: MediaError | Record<string, unknown> | null) => {
    const videoUrl = video?.currentSrc || sources[0]?.src || '';

    return JSON.stringify({
      version: 1,
      content_id: contentId,
      file_id: fileId,
      started_at: new Date(startedAt).toISOString(),
      reported_at: new Date().toISOString(),
      elapsed_ms: Date.now() - startedAt,
      source: {
        current_url: videoUrl,
        candidates: sources,
      },
      counters,
      buffering: {
        max_buffered_ahead_sec: round(maxBufferedAheadSec),
        total_buffering_ms: totalBufferingMs,
        max_buffering_ms: maxBufferingMs,
        is_buffering_now: bufferingStartedAt !== null,
      },
      current_state: video ? {
        current_time: round(video.currentTime),
        duration: Number.isFinite(video.duration) ? round(video.duration) : null,
        ready_state: video.readyState,
        network_state: video.networkState,
        paused: video.paused,
        ended: video.ended,
        playback_rate: video.playbackRate,
        buffered_ahead_sec: getBufferedAhead(video),
        buffered_ranges: getRanges(video.buffered),
        seekable_ranges: getRanges(video.seekable),
      } : null,
      playback_quality: getPlaybackQuality(video),
      network: {
        online: navigator.onLine,
        connection: getConnectionInfo(),
        resource_timing: getResourceTiming(videoUrl),
      },
      page: {
        href: window.location.href,
        visibility_state: document.visibilityState,
      },
      error: serializeError(error),
      events,
    });
  };

  return {
    addEvent,
    attach,
    toRawData,
  };
};

export default createSimplePlayerDiagnostics;
